import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { materialService } from "../../services/materialService";
import { adminService } from "../../services/adminService";
import {
  CubeIcon,
  CurrencyDollarIcon,
  TruckIcon,
  UserGroupIcon,
  PlusIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [sales, setSales] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [pendingValidations, setPendingValidations] = useState([]);
  const [validationHistory, setValidationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    sales: { totalRevenue: 0, totalSales: 0, paidSales: 0, processingSales: 0 },
    deliveries: { totalDeliveries: 0, delivered: 0, inStore: 0, totalFee: 0 },
    validations: { totalQuantity: 0, totalBins: 0, uniqueAgents: 0, uniqueStates: 0 }
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [materialsData, salesData, deliveriesData, validationsData, historyData] = await Promise.all([
          materialService.getAllMaterials(),
          adminService.getAllSales({ limit: 5 }),
          adminService.getAllDeliveries({ limit: 5 }),
          adminService.getPendingValidations({ limit: 5 }),
          adminService.getValidationHistory({ limit: 5 }),
        ]);

        setMaterials(materialsData);
        setSales(salesData.sales || []);
        setDeliveries(deliveriesData.deliveries || []);
        setPendingValidations(validationsData.validations || []);
        setValidationHistory(historyData.history || []);
        
        // Set stats from the enhanced endpoints
        setStats({
          sales: salesData.stats || { totalRevenue: 0, totalSales: 0, paidSales: 0, processingSales: 0 },
          deliveries: deliveriesData.stats || { totalDeliveries: 0, delivered: 0, inStore: 0, totalFee: 0 },
          validations: validationsData.stats || { totalQuantity: 0, totalBins: 0, uniqueAgents: 0, uniqueStates: 0 }
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const dashboardStats = [
    {
      name: "Total Materials",
      value: materials.length,
      icon: CubeIcon,
      color: "bg-blue-500",
      href: "/admin/materials",
    },
    {
      name: "Total Sales",
      value: stats.sales.totalSales,
      icon: CurrencyDollarIcon,
      color: "bg-green-500",
      href: "/admin/sales",
    },
    {
      name: "Total Deliveries",
      value: stats.deliveries.totalDeliveries,
      icon: TruckIcon,
      color: "bg-purple-500",
      href: "/admin/deliveries",
    },
    {
      name: "Total Revenue",
      value: `₦${stats.sales.totalRevenue?.toLocaleString() || '0'}`,
      icon: ChartBarIcon,
      color: "bg-orange-500",
      href: "/admin/sales",
    },
    {
      name: "Pending Validations",
      value: stats.validations.totalBins,
      icon: ClockIcon,
      color: "bg-yellow-500",
      href: "/admin/pending-validations",
    },
    {
      name: "Total Fee Earned",
      value: `₦${stats.deliveries.totalFee?.toLocaleString() || '0'}`,
      icon: CheckCircleIcon,
      color: "bg-indigo-500",
      href: "/admin/deliveries",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.first_name || user?.username}!
        </h1>
        <p className="text-gray-600 mt-2">
          You're the system administrator. Manage materials, monitor sales, and
          oversee deliveries.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardStats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stat.value}
                </p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/admin/materials"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CubeIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Manage Materials</h3>
              <p className="text-sm text-gray-600">
                Add, edit, or remove materials
              </p>
            </div>
          </Link>

          <Link
            to="/admin/sales"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CurrencyDollarIcon className="h-8 w-8 text-green-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">View All Sales</h3>
              <p className="text-sm text-gray-600">Monitor all user sales</p>
            </div>
          </Link>

          <Link
            to="/admin/deliveries"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <TruckIcon className="h-8 w-8 text-purple-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Track Deliveries</h3>
              <p className="text-sm text-gray-600">Monitor agent deliveries</p>
            </div>
          </Link>

          <Link
            to="/admin/pending-validations"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ClockIcon className="h-8 w-8 text-yellow-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Pending Validations</h3>
              <p className="text-sm text-gray-600">Review pending validations</p>
            </div>
          </Link>

          <Link
            to="/admin/validation-history"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <CheckCircleIcon className="h-8 w-8 text-indigo-600 mr-3" />
            <div>
              <h3 className="font-medium text-gray-900">Validation History</h3>
              <p className="text-sm text-gray-600">View validation history</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Sales */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Sales
          </h2>
          {sales.length > 0 ? (
            <div className="space-y-4">
              {sales.map((sale) => (
                <div
                  key={sale._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      Sale #{sale._id?.slice(-6) || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      User: {sale.user?.firstName && sale.user?.lastName 
                        ? `${sale.user.firstName} ${sale.user.lastName}` 
                        : sale.user?.username || "Unknown"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(sale.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      ₦{sale.totalPrice?.toLocaleString() || "0"}
                    </p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        sale.status === "paid"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {sale.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">
              No sales recorded yet.
            </p>
          )}
        </div>

        {/* Recent Deliveries */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Deliveries
          </h2>
          {deliveries.length > 0 ? (
            <div className="space-y-4">
              {deliveries.map((delivery) => (
                <div
                  key={delivery._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      Delivery #{delivery._id?.slice(-6) || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      Agent: {delivery.agent?.firstName && delivery.agent?.lastName 
                        ? `${delivery.agent.firstName} ${delivery.agent.lastName}` 
                        : delivery.agent?.username || "Unknown"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(delivery.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      Qty: {delivery.totalQuantity}kg
                    </p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        delivery.pickupStatus === "Delivered"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {delivery.pickupStatus}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">
              No deliveries recorded yet.
            </p>
          )}
        </div>

        {/* Pending Validations */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Pending Validations
          </h2>
          {pendingValidations.length > 0 ? (
            <div className="space-y-4">
              {pendingValidations.map((validation) => (
                <div
                  key={validation._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      Validation #{validation._id?.slice(-6) || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      Agent: {validation.agent?.firstName && validation.agent?.lastName 
                        ? `${validation.agent.firstName} ${validation.agent.lastName}` 
                        : validation.agent?.name || "Unknown"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(validation.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      Qty: {validation.totalQuantity}kg
                    </p>
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Pending
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">
              No pending validations.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
